import type { FC, RefObject } from "react";
import { useEffect, useRef } from "react";
import styles from "../agentChatDrawer.module.scss";
import RequestView from "@/layout/agentChat/components/RequestView.tsx";
import ResponseView from "@/layout/agentChat/components/ResponseView.tsx";
import type { AgentHistoryEntry } from "@/context/agent/types.ts";
import { useTranslation } from "react-i18next";
import AgentLoader from "@/layout/agentChat/components/AgentLoader.tsx";
import AgentEmptyState from "@/layout/agentChat/components/AgentEmptyState.tsx";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { SerializedError } from "@reduxjs/toolkit";

type AgentContentProps = {
  isLoading: boolean;
  error?: FetchBaseQueryError | SerializedError;
  errorMessage: string | null;
  history: AgentHistoryEntry[];
  listRef: RefObject<HTMLDivElement | null>;
  onSelectPrompt: (text: string) => void;
}

const AgentContent: FC<AgentContentProps> = ({ isLoading, error, errorMessage, history, listRef, onSelectPrompt }) => {
  const { t } = useTranslation("translation", { keyPrefix: "agentChat" });
  const lastEntryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof lastEntryRef.current?.scrollIntoView === 'function') {
      lastEntryRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }, [history.length]);

  return (
    <div ref={listRef} className={styles.content}>
      {history.length === 0 && !isLoading && !error ? <AgentEmptyState onSelectPrompt={onSelectPrompt} /> : null}

      {error ? (
        <div className={styles.error} role="alert">
          {errorMessage ?? t("error")}
        </div>
      ) : null}

      {history.length > 0 ? (
        <div className={styles.list}>
          {history.map((entry, i) => (
            <div key={entry.id} ref={i === history.length - 1 ? lastEntryRef : null} className={styles.entry}>
              <RequestView entry={entry} />
              <ResponseView entry={entry} />
            </div>
          ))}
        </div>
      ) : null}

      {isLoading ? <AgentLoader /> : null}
    </div>
  );
};

export default AgentContent;
